import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Container, Row, Col, Button, Badge, Spinner, Alert, Table, Modal, Form, Pagination } from 'react-bootstrap';
import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { useNavigate } from "react-router-dom";
import '../AdminDashboard.css';
import './SubscriptionManagement.css';
import { refreshAllowedPagesCookie } from '../authCookies';
// Cognitive Flows credits are billing, so they live on this tab. The CF axios
// instance is module-level — no CognitiveFlowsProvider needed here, and its
// x-cf-workspace header defaults to 'account', which is what we want: the
// credit balance is account-level and every workspace draws from it.
import cfApi, { setCognitiveFlowsApiBase } from '../cognitive-flows/utils/api';
// The active plan renders through the SAME card the account-setup wizard and
// the Cognitive Flows plan catalogue use. Three renderers for one price is how
// they end up disagreeing about what a customer pays.
import PlanCard from '../components/billing/PlanCard';
import TopUpCreditsModal from '../cognitive-flows/components/billing/TopUpCreditsModal';
import { purchaseCredits } from '../cognitive-flows/utils/creditTopUp';
import { capture, planEventName } from '../utils/analytics';
import { UPGRADE_PLAN_ROUTE } from '../utils/pageRoutes';
// The SAME carousel + card the account-setup wizard's step 3 renders, so an
// upgrade offer cannot drift into looking like a different product from the
// one the customer was shown at signup.
import PlanCarousel from '../components/billing/PlanCarousel';

// One accent for everything billing renders — the plan card's tick marks, the
// credit balance and the Top up button all read as the same surface. Matches
// the app-wide primary navy (.sxi_primary_blue_btn) so this tab stops looking
// like a foreign surface.
const BILLING_ACCENT = '#132848';

// Billing on this page is the Cognitive Agents product. The plan catalogue is
// shared with the landing-page products (keyword/layout personalization), and
// `GET /api/subscriptions/plans` returns all of them, so anything that offers a
// plan to a customer here has to say which family it means. Matches
// AGENT_PLAN_CATEGORY in services/cognitive-flows/agentPlan.js.
const AGENT_PLAN_CATEGORY = 'cognitive_agents';

/**
 * The plans this account could move to: the agent catalogue only, and only the
 * ones that actually work.
 *
 * A plan with no included runs BLOCKS every run its subscriber starts —
 * `consumeRun` refuses at a zero balance — so offering one is selling a switch
 * that turns the product off. `/settings/plans` already refuses to show those;
 * this is the same rule on the surface that had never learned it.
 */
const sellableAgentPlans = (plans) => (plans || []).filter((p) => (
    p?.is_active && p?.planCategory === AGENT_PLAN_CATEGORY && Number(p?.fees?.includedRuns) > 0
));

// An id can arrive as a string, an ObjectId, or the `{ $oid }` shape depending
// on which write path stored it. Module scope so every handler in this file can
// use it regardless of where it sits relative to the component body.
const normalizeId = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value.$oid) return value.$oid;
  if (typeof value?.toString === 'function') return value.toString();
  return null;
};

const SubscriptionManagement = ({ ViewedPage, app_url, isInSettingsContainer = false }) => {
  const [subscription, setSubscription] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [masterFeatures, setMasterFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [addingPaymentMethod, setAddingPaymentMethod] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const location = useLocation();
   const navigate = useNavigate(); // ✅ inside component
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradingPlanId, setUpgradingPlanId] = useState(null);
  const [invoicePage, setInvoicePage] = useState(1);
  const [billingInfo, setBillingInfo] = useState({
    company_name: '',
    gst_number: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  });
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingSaving, setBillingSaving] = useState(false);
  const [billingEditing, setBillingEditing] = useState(false);
  const user_email = Cookies.get('user_email');
  const shop_domain = Cookies.get('shop');

  // Cognitive Flows credit balance. Two different things can add to it, and the
  // page shows each only to whoever the server will let do it: an owner BUYS
  // credits (Razorpay, below), while platform staff can GRANT them outright
  // (POST /settings/credits/add is requireAdminOrDeveloper).
  const user_role = Cookies.get('user_role') || '';
  const isPlatformStaff = user_role === 'admin' || user_role === 'developer';
  const [credits, setCredits] = useState(null);
  const [addCreditsAmount, setAddCreditsAmount] = useState(10);
  const [addingCredits, setAddingCredits] = useState(false);
  // Buying credits is owner-only (POST /settings/credits/create-order is
  // requireAccountOwner). The server decides — asking /meta rather than reading
  // a role cookie keeps the button off screens where it would only ever 403.
  const [canBuyCredits, setCanBuyCredits] = useState(false);
  // A free (Starter) plan cannot buy credits at all — the server refuses the
  // order with UPGRADE_REQUIRED — so the owner is pointed at an upgrade instead
  // of a button that would 403. Separate from `canBuyCredits` because the two
  // reasons need different sentences: "you aren't the owner" and "your plan
  // doesn't include this" are not the same problem.
  const [planIsFree, setPlanIsFree] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUpLoading, setTopUpLoading] = useState(false);

  // Accordion-style open/close state for main sections
  const [openSections, setOpenSections] = useState({
    activePlan: true,
    paymentMethods: true,
    billingInfo: true,
    invoices: true,
    credits: true
  });

  const toggleSection = (key) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Credit balance. A shop that has never used Cognitive Flows still answers
  // here (credits default to 0); a failure leaves `credits` null so the section
  // says so rather than claiming a zero balance.
  useEffect(() => {
    if (app_url) setCognitiveFlowsApiBase(app_url);
    cfApi
      .get('/settings')
      // The section is LABELLED "Available credits" and was showing purchased
      // ones — the plan allowance, which is most of a new account's balance,
      // was missing from the figure entirely.
      .then((r) => setCredits(r.data.availableCredits ?? r.data.credits ?? 0))
      .catch(() => setCredits(null));
    cfApi
      .get('/meta')
      .then((r) => {
        setCanBuyCredits(r.data?.can?.manageBilling === true);
        setPlanIsFree(r.data?.planIsFree === true);
      })
      .catch(() => {
        setCanBuyCredits(false);
        setPlanIsFree(false);
      });
  }, [app_url]);

  const handleTopUp = async (creditsToBuy) => {
    setTopUpLoading(true);
    try {
      const data = await purchaseCredits(creditsToBuy);
      setCredits(data.credits ?? data.data?.balanceAfter ?? credits);
      setTopUpOpen(false);
      toast.success(`${creditsToBuy} credits added`);
    } catch (err) {
      // A dismissed checkout is a decision, not a failure — say so plainly and
      // leave the modal open so the amount is still there to retry.
      const message = err?.message === 'Payment cancelled'
        ? 'Payment cancelled'
        : (err?.response?.data?.error || err?.message || 'Payment was not completed');
      toast.error(message);
    } finally {
      setTopUpLoading(false);
    }
  };

  const handleAddCredits = async () => {
    if (!(addCreditsAmount > 0)) return toast.error('Enter a credit amount above zero');
    setAddingCredits(true);
    try {
      const { data } = await cfApi.post('/settings/credits/add', { amount: addCreditsAmount });
      setCredits(data.credits);
      toast.success(`Added ${addCreditsAmount} credits`);
    } catch {
      toast.error('Failed to add credits');
    } finally {
      setAddingCredits(false);
    }
  };

  const loadRazorpay = () => {
    if (window.Razorpay) return Promise.resolve(true);

    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) {
      return new Promise((resolve, reject) => {
        const onLoad = () => resolve(true);
        const onError = () => reject(new Error('Failed to load Razorpay'));
        existing.addEventListener('load', onLoad, { once: true });
        existing.addEventListener('error', onError, { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error('Failed to load Razorpay'));
      document.body.appendChild(script);
    });
  };

  const fetchSubscription = useCallback(async () => {
    try {
      const params = shop_domain ? `?shop_domain=${shop_domain}` : '';
      const response = await axios.get(`${app_url}/api/subscriptions/my-subscription${params}`, {
        withCredentials: true
      });
      if (response.data.success) {
        if (response.data.data || !location.state?.subscription) {
          setSubscription(response.data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      toast.error('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  }, [app_url, shop_domain, location.state?.subscription]);

  const fetchPaymentMethods = useCallback(async () => {
    try {
      const params = shop_domain ? `?shop_domain=${shop_domain}` : '';
      const response = await axios.get(`${app_url}/api/subscriptions/payment-methods${params}`, {
        withCredentials: true
      });
      if (response.data.success) {
        setPaymentMethods(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  }, [app_url, shop_domain]);

  const fetchInvoices = useCallback(async () => {
    try {
      const params = shop_domain ? `?shop_domain=${shop_domain}` : '';
      const response = await axios.get(`${app_url}/api/subscriptions/invoices${params}`, {
        withCredentials: true
      });
      if (response.data.success) {
        setInvoices(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  }, [app_url, shop_domain]);

  const fetchAvailablePlans = useCallback(async () => {
    try {
      const params = shop_domain ? `?shop_domain=${shop_domain}` : '';
      const response = await axios.get(`${app_url}/api/subscriptions/plans${params}`, {
        withCredentials: true
      });
      if (response.data.success) {
        setAvailablePlans(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching available plans:', error);
    }
  }, [app_url, shop_domain]);

  const fetchMasterFeatures = useCallback(async () => {
    try {
      const response = await axios.get(`${app_url}/api/subscriptions/master-features`, {
        withCredentials: true
      });
      if (response.data.success) {
        setMasterFeatures(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching master features:', error);
    }
  }, [app_url]);

  const refreshSubscriptionAndInvoices = () => {
    // Immediate refresh + short delayed refresh to catch webhook/eventual consistency updates.
    fetchSubscription();
    fetchInvoices();
    setTimeout(() => fetchSubscription(), 2000);
    setTimeout(() => fetchInvoices(), 2000);
    setTimeout(() => fetchSubscription(), 5000);
    setTimeout(() => fetchInvoices(), 5000);
  };

  const fetchBillingInfo = useCallback(async () => {
    try {
      setBillingLoading(true);
      const params = shop_domain ? `?shop_domain=${shop_domain}` : '';
      const response = await axios.get(`${app_url}/api/subscriptions/billing-info${params}`, {
        withCredentials: true
      });
      if (response.data.success && response.data.data) {
        setBillingInfo(prev => ({
          ...prev,
          ...response.data.data
        }));
      }
    } catch (error) {
      console.error('Error fetching billing info:', error);
    } finally {
      setBillingLoading(false);
    }
  }, [app_url, shop_domain]);

  useEffect(() => {
    ViewedPage(103);
    fetchSubscription();
    fetchPaymentMethods();
    fetchInvoices();
    fetchAvailablePlans();
    fetchMasterFeatures();
    fetchBillingInfo();

    if (location.state?.promptAddPayment) {
      toast.success('Free plan activated! Please add a payment method under subscription management to save a card for future upgrades.');
      setShowAddPaymentModal(true);
    }

    if (location.state?.subscription) {
      setSubscription(location.state.subscription);
    }
  }, [
    ViewedPage,
    fetchSubscription,
    fetchPaymentMethods,
    fetchInvoices,
    fetchAvailablePlans,
    fetchMasterFeatures,
    fetchBillingInfo,
    location.state?.promptAddPayment,
    location.state?.subscription
  ]);

  const handleBillingChange = (field, value) => {
    setBillingInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBillingSave = async (e) => {
    e.preventDefault();
    try {
      setBillingSaving(true);
      const payload = {
        ...billingInfo,
        shop_domain: shop_domain
      };
      const response = await axios.post(`${app_url}/api/subscriptions/billing-info`, payload, {
        withCredentials: true
      });
      if (response.data.success) {
        toast.success('Billing details updated');
        if (response.data.data) {
          setBillingInfo(prev => ({
            ...prev,
            ...response.data.data
          }));
        }
        setBillingEditing(false);
      } else {
        toast.error(response.data.message || 'Failed to update billing details');
      }
    } catch (error) {
      console.error('Error updating billing info:', error);
      toast.error(error?.response?.data?.message || 'Error updating billing details');
    } finally {
      setBillingSaving(false);
    }
  };

  const handleUpgrade = async (newPlan) => {
    capture(planEventName(newPlan, 'clicked'), {
      plan_id: newPlan._id,
      plan_name: newPlan.name,
      price: newPlan.price,
      currency: newPlan.currency,
      surface: 'settings_billing',
      action: 'upgrade',
    });
    if (!window.confirm(`Are you sure you want to upgrade to ${newPlan.name}? This will start a fresh billing cycle with full plan payment.`)) {
      return;
    }

    setUpgradingPlanId(newPlan?._id || null);
    setUpgrading(true);
    try {
      const response = await axios.post(`${app_url}/api/subscriptions/upgrade`, {
        new_plan_id: newPlan._id,
        shop_domain: shop_domain,
        billing_mode: 'fresh'
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        const {
          razorpay_subscription_id,
          razorpay_key_id,
          order_id,
          amount,
          new_plan_id,
          requires_payment,
          is_free_plan
        } = response.data.data;

        const captureUpgraded = () => capture(planEventName(newPlan, 'subscribed'), {
          plan_id: newPlan._id,
          plan_name: newPlan.name,
          surface: 'settings_billing',
          action: 'upgrade',
          is_free: is_free_plan === true,
        });

        if (is_free_plan) {
          captureUpgraded();
          toast.success('Subscription upgraded successfully!');
          setShowUpgradeModal(false);
          refreshSubscriptionAndInvoices();
          setUpgrading(false);
          setUpgradingPlanId(null);
          return;
        }

        if (!requires_payment) {
          captureUpgraded();
          toast.success(response.data.message || 'Subscription upgraded successfully!');
          setShowUpgradeModal(false);
          refreshSubscriptionAndInvoices();
          setUpgrading(false);
          setUpgradingPlanId(null);
          return;
        }

        if (order_id && amount) {
          await loadRazorpay();
          {
            const options = {
              key: razorpay_key_id,
              order_id: order_id,
              amount: amount,
              name: 'ScaleX AI',
              description: `Upgrade to ${newPlan.name}`,
              image: '/logo.png',
              prefill: { email: user_email },
              theme: { color: '#3399cc' },
              config: {
                display: {
                  blocks: {
                    upi: { name: 'UPI', instruments: [{ method: 'upi', flows: ['collect', 'intent', 'qr'] }] },
                    cards: { name: 'Cards', instruments: [{ method: 'card' }] }
                  },
                  sequence: ['block.upi', 'block.cards'],
                  preferences: { show_default_blocks: false }
                }
              },
              handler: async function () {
                try {
                  await axios.post(`${app_url}/api/subscriptions/upgrade/confirm`, {
                    order_id: order_id,
                    new_plan_id: new_plan_id || newPlan._id,
                    shop_domain: shop_domain
                  }, { withCredentials: true });
                  captureUpgraded();
                  toast.success('Subscription upgraded successfully!');
                  setShowUpgradeModal(false);
                  refreshSubscriptionAndInvoices();
                  await refreshAllowedPagesCookie(app_url, shop_domain);
                } catch (err) {
                  toast.error(err.response?.data?.message || 'Upgrade confirmation failed');
                } finally {
                  setUpgrading(false);
                  setUpgradingPlanId(null);
                }
              },
              modal: {
                ondismiss: function () {
                  setUpgrading(false);
                  setUpgradingPlanId(null);
                  toast.error('Upgrade cancelled');
                }
              }
            };
            const razorpay = new window.Razorpay(options);
            razorpay.open();
          }
          return;
        }

        if (razorpay_subscription_id) {
          await loadRazorpay();
          {
            const options = {
              key: razorpay_key_id,
              subscription_id: razorpay_subscription_id,
              name: 'ScaleX AI',
              description: `Upgrade to ${newPlan.name}`,
              image: '/logo.png',
              prefill: { email: user_email },
              theme: { color: '#3399cc' },
              handler: function () {
                captureUpgraded();
                toast.success('Subscription upgraded successfully!');
                setShowUpgradeModal(false);
                refreshSubscriptionAndInvoices();
                setUpgrading(false);
                setUpgradingPlanId(null);
              },
              modal: {
                ondismiss: function () {
                  setUpgrading(false);
                  setUpgradingPlanId(null);
                  toast.error('Upgrade cancelled');
                }
              }
            };
            const razorpay = new window.Razorpay(options);
            razorpay.open();
          }
        } else {
          setUpgrading(false);
          setUpgradingPlanId(null);
        }
      } else {
        toast.error(response.data.message || 'Failed to upgrade subscription');
        setUpgrading(false);
        setUpgradingPlanId(null);
      }
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      toast.error(error.response?.data?.message || 'Failed to upgrade subscription');
      setUpgrading(false);
      setUpgradingPlanId(null);
    }
  };

  // A plan's master features, resolved to names, with unresolvable ids dropped.
  const planFeatureNames = (plan) => (
    (Array.isArray(plan?.masterFeatures) ? plan.masterFeatures : [])
      .map((id) => masterFeatures.find((f) => normalizeId(f._id) === normalizeId(id))?.name)
      .filter(Boolean)
  );

  /**
   * A raw `subscription_plans` doc in the shape PlanCard reads.
   *
   * The upgrade modal used to hand-roll its own bootstrap Card: "$89.00 /
   * monthly" against the wizard's "$89 / month", bullets in a bordered scroll
   * box instead of ticks, no Most Popular badge, no "was" price. The same plan
   * therefore looked like two different products depending on where you met it.
   * Mapping onto the shared card is what makes them identical rather than
   * merely similar.
   *
   * `price` is major units and is DERIVED server-side from the fee block
   * (agentPlan.js writes `agentPlanChargeMinorUnits(fees, interval) / 100` back
   * into it), so `price * 100` is exactly the `chargeMinorUnits` that
   * /settings/plans sends the wizard — not a second opinion about the price.
   */
  const toPlanCardModel = (plan) => ({
    id: normalizeId(plan._id),
    name: plan.name,
    description: plan.description || '',
    currency: plan.currency || 'INR',
    interval: plan.interval || 'monthly',
    chargeMinorUnits: Math.round((Number(plan.price) || 0) * 100),
    includedRuns: Number(plan.fees?.includedRuns) || 0,
    includedCredits: Number(plan.fees?.includedCredits) || 0,
    annualDiscountPct: Number(plan.fees?.annualDiscountPct) || 0,
    originalPriceMinorUnits: Number(plan.fees?.originalPriceMinorUnits) || 0,
    isPopular: plan.fees?.isPopular === true,
    // Unchanged rule: resolved names only, unresolvable ids dropped.
    highlights: planFeatureNames(plan),
    // getUpgradeablePlans() excludes the plan the shop is already on, so no
    // card here can ever be the current one.
    isCurrent: false,
  });

  const getUpgradeablePlans = () => {
    if (!subscription || !availablePlans.length) return [];

    const currentPlanPrice = subscription.plan_price || 0;
    return sellableAgentPlans(availablePlans).filter((plan) => {
      if (normalizeId(plan._id) === normalizeId(subscription.plan_id)) return false;
      return plan.price > currentPlanPrice;
    }).sort((a, b) => a.price - b.price);
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    setCancelling(true);
    try {
      const response = await axios.post(`${app_url}/api/subscriptions/cancel`, {
        subscription_id: subscription._id,
        shop_domain: shop_domain
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        toast.success('Subscription cancelled successfully');
        fetchSubscription();
        await refreshAllowedPagesCookie(app_url, shop_domain);
        navigate('/pricing');  // 👈 redirect to pricing page
      } else {
        toast.error(response.data.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(error.response?.data?.message || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    setAddingPaymentMethod(true);
    try {
      const response = await axios.post(`${app_url}/api/subscriptions/payment-methods/create-auth-order`, {
        shop_domain: shop_domain,
        // Verify the card in the currency this account is actually billed in.
        // Without the plan the server falls back to INR, which shows a rupee
        // sheet to a customer on a dollar plan.
        plan_id: normalizeId(subscription?.plan?._id || subscription?.plan_id) || undefined
      }, {
        withCredentials: true
      });
      if (response.data.success) {
        const { order_id, amount, currency, razorpay_key_id } = response.data.data;
        await loadRazorpay();
        {
          // Convert amount from smallest currency unit (paise/cents) to main unit
          const displayAmount = amount / 100;
          const options = {
            key: razorpay_key_id,
            order_id,
            amount,
            currency: currency || 'INR',
            name: 'ScaleX AI',
            description: `Add payment method (${formatCurrency(displayAmount, currency)} authorization - refundable)`,
            image: '/logo.png',
            prefill: { email: user_email },
            theme: { color: '#3399cc' },
            config: {
              display: {
                blocks: {
                  upi: { name: 'UPI', instruments: [{ method: 'upi', flows: ['collect', 'intent', 'qr'] }] },
                  cards: { name: 'Cards', instruments: [{ method: 'card' }] }
                },
                sequence: ['block.upi', 'block.cards'],
                preferences: { show_default_blocks: false }
              }
            },
            handler: async function (response) {
              try {
                await axios.post(`${app_url}/api/subscriptions/payment-methods/save`, {
                  payment_id: response.razorpay_payment_id,
                  shop_domain: shop_domain
                }, { withCredentials: true });
              } catch (err) {
                console.error('Error saving payment method manually', err);
              }
              setAddingPaymentMethod(false);
              setShowAddPaymentModal(false);
              toast.success('Payment method added successfully! Card saved securely.');
              fetchPaymentMethods();
            },
            modal: {
              ondismiss: function () {
                setAddingPaymentMethod(false);
                toast.error('Payment cancelled');
              }
            }
          };
          const razorpay = new window.Razorpay(options);
          razorpay.open();
        }
      } else {
        toast.error(response.data.message || 'Failed to create payment');
        setAddingPaymentMethod(false);
      }
    } catch (error) {
      console.error('Error adding payment method:', error);
      toast.error(error.response?.data?.message || 'Failed to add payment method');
      setAddingPaymentMethod(false);
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId) => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) {
      return;
    }

    try {
      const response = await axios.post(`${app_url}/api/subscriptions/payment-methods/delete`, {
        payment_method_id: paymentMethodId,
        shop_domain: shop_domain
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        toast.success('Payment method deleted successfully');
        fetchPaymentMethods();
      } else {
        toast.error(response.data.message || 'Failed to delete payment method');
      }
    } catch (error) {
      console.error('Error deleting payment method:', error);
      toast.error(error.response?.data?.message || 'Failed to delete payment method');
    }
  };

  const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
    try {
      const response = await axios.post(`${app_url}/api/subscriptions/payment-methods/set-default`, {
        payment_method_id: paymentMethodId,
        shop_domain: shop_domain
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        toast.success('Default payment method updated');
        fetchPaymentMethods();
      } else {
        toast.error(response.data.message || 'Failed to update default payment method');
      }
    } catch (error) {
      console.error('Error setting default payment method:', error);
      toast.error(error.response?.data?.message || 'Failed to update default payment method');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { variant: 'success', text: 'Active' },
      pending: { variant: 'warning', text: 'Pending' },
      cancelled: { variant: 'danger', text: 'Cancelled' },
      paused: { variant: 'secondary', text: 'Paused' },
      created: { variant: 'info', text: 'Created' },
      authenticated: { variant: 'primary', text: 'Authenticated' }
    };

    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency) => {
    const symbol = currency === 'INR' ? '₹' : '$';
    return `${symbol}${amount.toFixed(2)}`;
  };

  const getCardBrand = (network) => {
    const brands = {
      'Visa': 'Visa',
      'MasterCard': 'Mastercard',
      'Amex': 'American Express',
      'Diners': 'Diners Club',
      'Discover': 'Discover',
      'JCB': 'JCB',
      'Maestro': 'Maestro',
      'Rupay': 'Rupay'
    };
    return brands[network] || network || 'Card';
  };

  const getNextBillingDate = () => {
    if (!subscription || !subscription.current_end) return null;
    return new Date(subscription.current_end);
  };

  // Credits are independent of the subscription, so this block renders in the
  // "No Active Subscription" branch too — an account can hold a balance before
  // it ever picks a plan.
  const creditsSection = (
    <div className="opportunities-section mb-4">
      <button
        type="button"
        className="opportunities-section-header"
        onClick={() => toggleSection('credits')}
        aria-expanded={openSections.credits}
      >
        <div>
          <h5 className="mb-1">Credits</h5>
        </div>
        <span
          className={`opportunities-section-chevron ${openSections.credits ? 'open' : ''}`}
          aria-hidden="true"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M4.5 6L8 9.5L11.5 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {openSections.credits && (
        <div className="opportunities-section-body">
          <div className="billing-body">
            {credits === null ? (
              <div className="billing-empty">Credit balance is unavailable right now.</div>
            ) : (
              <div className="billing-wallet">
                <div className="billing-wallet-figure">
                  <span className="billing-wallet-label">Available credits</span>
                  <span className="billing-wallet-value">{Number(credits).toLocaleString()}</span>
                  <p className="billing-wallet-note">
                    Every workflow run draws from this balance. All workspaces in the account share it.
                  </p>
                </div>

                <div className="billing-wallet-action">
                  {canBuyCredits && !planIsFree && (
                    <Button
                      className="sxi_primary_blue_btn btn-height"
                      onClick={() => {
                        capture('add_credits_clicked', { surface: 'subscription_management' });
                        setTopUpOpen(true);
                      }}
                    >
                      Top up
                    </Button>
                  )}
                  {canBuyCredits && planIsFree && (
                    <p className="billing-wallet-note mb-0">
                      Your free plan doesn&apos;t include credit top-ups.{' '}
                      <Link to={UPGRADE_PLAN_ROUTE}>Upgrade your plan</Link> for a
                      bigger monthly credit allowance.
                    </p>
                  )}
                  {!canBuyCredits && (
                    <p className="billing-wallet-note mb-0">
                      {/* The link used to point at the Cognitive Flows billing
                          page, which is retired. Nothing replaces it: the
                          balance it offered to go and show is displayed
                          directly above this sentence. */}
                      Only the account owner can buy credits. You can see the balance
                      and usage above.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Granting credits without a payment is a platform-staff action
                (POST /settings/credits/add is requireAdminOrDeveloper), separate
                from the customer-facing purchase above. */}
            {isPlatformStaff && (
              <div className="billing-staff-grant">
                <span className="billing-rail-label">Grant credits</span>
                <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                  <Form.Control
                    type="number"
                    min={1}
                    value={addCreditsAmount}
                    onChange={(e) => setAddCreditsAmount(Number(e.target.value))}
                    style={{ maxWidth: '120px' }}
                    aria-label="Credits to grant"
                  />
                  <Button
                    className="back-button btn-height"
                    onClick={handleAddCredits}
                    disabled={addingCredits}
                  >
                    {addingCredits ? 'Adding' : 'Add credits'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <TopUpCreditsModal
        open={topUpOpen}
        defaultCredits={100}
        loading={topUpLoading}
        onClose={() => setTopUpOpen(false)}
        onSubmit={handleTopUp}
      />
    </div>
  );

  if (loading) {
    return (
      <div className={isInSettingsContainer ? 'subscription-management-container' : 'main-container'}>
        <div className="admin-loading-inline">
          <div className="admin-loading-content">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading subscription details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className={isInSettingsContainer ? 'subscription-management-container' : 'main-container subscription-management-container'}>
        <div className="admin-header">
          <div className="main-title">
              <h2>No Active Subscription</h2>
          </div>
          <p className="admin-subtitle">You don't have an active subscription</p>
        </div>
        <Container fluid className="admin-content px-3">
          <div className="admin-section">
            <Alert variant="info" className="mb-0">
              <Alert.Heading>Get Started</Alert.Heading>
              <p className="mb-3">Please subscribe to a plan to get started with ScaleX AI.</p>
              <Button href="/pricing" className="sxi_primary_blue_btn btn-height">
                View Pricing Plans
              </Button>
            </Alert>
          </div>
          <div className="mt-4">{creditsSection}</div>
        </Container>
      </div>
    );
  }

  const nextBillingDate = getNextBillingDate();

  // Prefer the joined plan document for display (keeps UI in sync with admin/pricing),
  // but fall back to legacy fields on the subscription record if needed.
  const effectivePlan = subscription.plan
    ? {
        name: subscription.plan.name,
        price: subscription.plan.price,
        currency: subscription.plan.currency,
        interval: subscription.plan.interval,
      }
    : {
        name: subscription.plan_name,
        price: subscription.plan_price,
        currency: subscription.plan_currency,
        interval: subscription.plan_interval,
      };
    // 👇 Add this line
   const displayStatus = (subscription.status === 'created' && effectivePlan.price === 0) ? 'active' : subscription.status;

  // The plan a free trial rolls onto — named to the customer as "Moves to X at
  // Y a month", so it has to be a plan they could actually be moved to. Reading
  // the whole catalogue promised them "mini2 at ₹0.03 a month", a
  // keyword-personalization plan that grants none of the agent pages this
  // account is using.
  const lowestPaidPlan = sellableAgentPlans(availablePlans)
    .filter((p) => p.price > 0)
    .sort((a, b) => a.price - b.price)[0] || null;

  // The plan, in the same card the account-setup wizard and the Cognitive Flows
  // catalogue draw. Everything here comes off this account's own subscription —
  // the card is a renderer, not a second source of truth about the price.
  const planFees = subscription.plan?.fees || {};
  const planHighlights = (Array.isArray(subscription.plan?.masterFeatures) ? subscription.plan.masterFeatures : [])
    .map((featureId) => masterFeatures.find((f) => normalizeId(f._id) === normalizeId(featureId))?.name)
    .filter(Boolean);

  const activePlanCard = {
    id: normalizeId(subscription.plan?._id) || 'active-plan',
    name: effectivePlan.name || 'Your plan',
    description: subscription.plan?.description || '',
    currency: effectivePlan.currency || 'INR',
    interval: effectivePlan.interval || 'monthly',
    chargeMinorUnits: Math.round((Number(effectivePlan.price) || 0) * 100),
    includedRuns: Number(planFees.includedRuns) || 0,
    includedCredits: Number(planFees.includedCredits) || 0,
    annualDiscountPct: Number(planFees.annualDiscountPct) || 0,
    highlights: planHighlights,
  };

  const invoicesPerPage = 8;
  const totalInvoicePages = Math.max(1, Math.ceil(invoices.length / invoicesPerPage));
  const safeInvoicePage = Math.min(invoicePage, totalInvoicePages);
  const paginatedInvoices = invoices.slice(
    (safeInvoicePage - 1) * invoicesPerPage,
    safeInvoicePage * invoicesPerPage
  );

  return (
    <div className={isInSettingsContainer ? 'subscription-management-container' : 'main-container subscription-management-container'}>
      <div className="opportunities-header" style={isInSettingsContainer ? {display: 'none'} : {}}>
        <div className="opportunities-header-inner">
          <div className="main-title">
            <h2 style={{marginBottom:0}}>Billing & Payments</h2>
          </div>
        </div>
      </div>

      <Container fluid className="p-0 mt-3">
        <div className="admin-content px-3">

        {/* Current Subscription / Active Plan */}
        <div className="opportunities-section mb-4">
          <button
            type="button"
            className="opportunities-section-header"
            onClick={() => toggleSection('activePlan')}
            aria-expanded={openSections.activePlan}
          >
            <div>
              <h5 className="mb-1">Active Plan</h5>
            </div>
            <span
              className={`opportunities-section-chevron ${openSections.activePlan ? 'open' : ''}`}
              aria-hidden="true"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4.5 6L8 9.5L11.5 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
          {openSections.activePlan && (
            <div className="opportunities-section-body">
              <div className="billing-plan-layout">
                <div className="billing-plan-card">
                  <PlanCard plan={activePlanCard} accent={BILLING_ACCENT} />
                </div>

                <div className="billing-plan-rail">
                  <div className="billing-rail-facts">
                    <div className="billing-rail-row">
                      <span className="billing-rail-label">Status</span>
                      <span>{getStatusBadge(displayStatus)}</span>
                    </div>

                    {/*{nextBillingDate && subscription.status === 'active' && (*/}
                    {/*  subscription.plan_price === 0 && lowestPaidPlan ? (*/}
                    {/*    <div className="billing-rail-row">*/}
                    {/*      <span className="billing-rail-label">Switches on</span>*/}
                    {/*      <span className="billing-rail-value">{formatDate(nextBillingDate)}</span>*/}
                    {/*      <p className="billing-rail-note">*/}
                    {/*        Moves to <strong>{lowestPaidPlan.name}</strong> at{' '}*/}
                    {/*        {formatCurrency(lowestPaidPlan.price, lowestPaidPlan.currency)} a month.*/}
                    {/*      </p>*/}
                    {/*    </div>*/}
                    {/*  ) : (*/}
                    {/*    <div className="billing-rail-row">*/}
                    {/*      <span className="billing-rail-label">Next billing date</span>*/}
                    {/*      <span className="billing-rail-value">{formatDate(nextBillingDate)}</span>*/}
                    {/*    </div>*/}
                    {/*  )*/}
                    {/*)}*/}
                  </div>

                  {displayStatus === 'active' && (
                    <div className="billing-rail-actions">
                      {getUpgradeablePlans().length > 0 && (
                        <Button className="sxi_primary_blue_btn btn-height" onClick={() => setShowUpgradeModal(true)}>
                          Upgrade plan
                        </Button>
                      )}
                      <Button className="back-button btn-height" onClick={handleCancel} disabled={cancelling}>
                        {cancelling ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Cancelling
                          </>
                        ) : (
                          'Cancel subscription'
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Cognitive Flows credits */}
        {creditsSection}

        {/* Payment Methods */}
        <div className="opportunities-section mb-4">
          <button
            type="button"
            className="opportunities-section-header"
            onClick={() => toggleSection('paymentMethods')}
            aria-expanded={openSections.paymentMethods}
          >
            <div>
              <h5 className="mb-1">Payment Methods</h5>
            </div>
            <span
              className={`opportunities-section-chevron ${openSections.paymentMethods ? 'open' : ''}`}
              aria-hidden="true"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4.5 6L8 9.5L11.5 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
          {openSections.paymentMethods && (
            <div className="opportunities-section-body">
              <div className='px-4'>
                  <div className="d-flex justify-content-start mb-3">
                    <Button
                      className="back-button btn-height"
                      onClick={() => setShowAddPaymentModal(true)}
                    >
                      Add payment method
                    </Button>
                  </div>
                  {paymentMethods.length === 0 ? (
                    <div className="payment-methods-empty">
                    </div>
                  ) : (
                    <div className="payment-methods-list">
                      {paymentMethods.map((method) => (
                        <div key={method._id} className="payment-method-item d-flex justify-content-between align-items-center py-3 border-bottom">
                          <div className="d-flex align-items-center">
                            <div className="me-3">
                              <strong>{getCardBrand(method.network)}</strong> •••• {method.last4}
                            </div>
                            {method.expiry_month && method.expiry_year && (
                              <span className="text-muted me-3">
                                Expires {String(method.expiry_month).padStart(2, '0')}/{method.expiry_year}
                              </span>
                            )}
                            {method.is_default && (
                              <Badge bg="primary" className="me-2">Default</Badge>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {!method.is_default && (
                              <Button
                                className="back-button btn-height"
                                onClick={() => handleSetDefaultPaymentMethod(method._id)}
                              >
                                Set as default
                              </Button>
                            )}
                            <Button
                              className="back-button btn-height text-danger"
                              onClick={() => handleDeletePaymentMethod(method._id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>

        {/* Billing Information */}
        <div className="opportunities-section mb-4">
          <button
            type="button"
            className="opportunities-section-header"
            onClick={() => toggleSection('billingInfo')}
            aria-expanded={openSections.billingInfo}
          >
            <div>
              <h5 className="mb-1">Billing Information</h5>
            </div>
            <span
              className={`opportunities-section-chevron ${openSections.billingInfo ? 'open' : ''}`}
              aria-hidden="true"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4.5 6L8 9.5L11.5 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
          {openSections.billingInfo && (
            <div className="opportunities-section-body">
              <div className='px-4'>
                  <Row>
                    <div className="billing-readonly mt-3 position-relative">
                      <div className="position-absolute top-0 end-0 px-3">
                        <Button className="back-button btn-height" onClick={() => setBillingEditing(true)}>
                          Edit
                        </Button>
                      </div>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Email</strong><br />
                        {user_email}
                      </p>
                    </Col>
                    </div>
                  </Row>
                  {billingLoading ? (
                    <div className="admin-loading-inline mt-3">
                      <div className="admin-loading-content">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 mb-0">Loading billing details...</p>
                      </div>
                    </div>
                  ) : billingEditing ? (
                    <Form className="mt-3" onSubmit={handleBillingSave}>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Company / Business name</Form.Label>
                            <Form.Control
                              type="text"
                              value={billingInfo.company_name}
                              onChange={(e) => handleBillingChange('company_name', e.target.value)}
                              placeholder="Enter company or legal entity name"
                            />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>GST number</Form.Label>
                            <Form.Control
                              type="text"
                              value={billingInfo.gst_number}
                              onChange={(e) => handleBillingChange('gst_number', e.target.value)}
                              placeholder="Enter GSTIN (optional)"
                            />
                            <Form.Text muted>
                              This GST number will appear on your invoices if provided.
                            </Form.Text>
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>Address line 1</Form.Label>
                            <Form.Control
                              type="text"
                              value={billingInfo.address_line1}
                              onChange={(e) => handleBillingChange('address_line1', e.target.value)}
                              placeholder="Street address, building, etc."
                            />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>Address line 2</Form.Label>
                            <Form.Control
                              type="text"
                              value={billingInfo.address_line2}
                              onChange={(e) => handleBillingChange('address_line2', e.target.value)}
                              placeholder="Apartment, suite, etc. (optional)"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>City</Form.Label>
                            <Form.Control
                              type="text"
                              value={billingInfo.city}
                              onChange={(e) => handleBillingChange('city', e.target.value)}
                            />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>State / Province</Form.Label>
                            <Form.Control
                              type="text"
                              value={billingInfo.state}
                              onChange={(e) => handleBillingChange('state', e.target.value)}
                            />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>Postal code</Form.Label>
                            <Form.Control
                              type="text"
                              value={billingInfo.postal_code}
                              onChange={(e) => handleBillingChange('postal_code', e.target.value)}
                            />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>Country</Form.Label>
                            <Form.Control
                              type="text"
                              value={billingInfo.country}
                              onChange={(e) => handleBillingChange('country', e.target.value)}
                              placeholder="India"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <div className="d-flex justify-content-end gap-2">
                        <Button
                          className="back-button btn-height"
                          onClick={() => setBillingEditing(false)}
                          disabled={billingSaving}
                        >
                          Cancel
                        </Button>
                        <Button
                          className="sxi_primary_blue_btn btn-height"
                          type="submit"
                          disabled={billingSaving}
                        >
                          {billingSaving ? 'Saving...' : 'Save billing details'}
                        </Button>
                      </div>
                    </Form>
                  ) : (
                    <div className="billing-readonly mt-3 position-relative">
                      <Row className="mt-2">
                        <Col md={12}>
                          <Row>
                            <Col md={6}><p><strong>Company / Business name</strong><br />{billingInfo.company_name || '-'}</p></Col>
                            <Col md={6}><p><strong>GST number</strong><br />{billingInfo.gst_number || 'Enter GSTIN (optional)'}</p></Col>
                          </Row>
                          <Row>
                            <Col md={6}><p><strong>Address line 1</strong><br />{billingInfo.address_line1 || '-'}</p></Col>
                            <Col md={6}><p><strong>Address line 2</strong><br />{billingInfo.address_line2 || '-'}</p></Col>
                          </Row>
                          <Row>
                            <Col md={6}><p><strong>City</strong><br />{billingInfo.city || '-'}</p></Col>
                            <Col md={6}><p><strong>State / Province</strong><br />{billingInfo.state || '-'}</p></Col>
                          </Row>
                          <Row>
                            <Col md={6}><p><strong>Postal code</strong><br />{billingInfo.postal_code || '-'}</p></Col>
                            <Col md={6}><p><strong>Country</strong><br />{billingInfo.country || '-'}</p></Col>
                          </Row>  
                        </Col>
                      </Row>
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>

        {/* Invoice History */}
        <div className="opportunities-section mb-4">
          <button
            type="button"
            className="opportunities-section-header"
            onClick={() => toggleSection('invoices')}
            aria-expanded={openSections.invoices}
          >
            <div>
              <h5 className="mb-1">Invoice History</h5>
            </div>
            <span
              className={`opportunities-section-chevron ${openSections.invoices ? 'open' : ''}`}
              aria-hidden="true"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4.5 6L8 9.5L11.5 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
          {openSections.invoices && (
            <div className="opportunities-section-body">
              <div>
                  {invoices.length === 0 ? (
                    <p className="text-muted mb-0">No invoices found.</p>
                  ) : (
                    <>
                      <div className="opportunities-keywords-table border rounded bg-white opp-padding">
                        <Table responsive className="mb-0">
                          <thead>
                            <tr>
                              <th className="channel_performance_header_cell">Date</th>
                              <th className="channel_performance_header_cell">Amount</th>
                              <th className="channel_performance_header_cell">Status</th>
                              <th className="channel_performance_header_cell">Description</th>
                              <th className="channel_performance_header_cell">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedInvoices.map((invoice, idx) => (
                              <tr key={invoice._id} className={idx % 2 === 0 ? 'channel_performance_table_row' : 'channel_performance_old_table_row'}>
                                <td className="channel_performance_table_cell">{formatDate(invoice.date)}</td>
                                <td className="channel_performance_table_cell">{formatCurrency(invoice.amount, invoice.currency)}</td>
                                <td className="channel_performance_table_cell">
                                  <Badge bg={invoice.status === 'captured' ? 'success' : invoice.status === 'failed' ? 'danger' : 'warning'}>
                                    {invoice.status === 'captured' ? 'Paid' : invoice.status}
                                  </Badge>
                                </td>
                                <td className="channel_performance_table_cell">{invoice.description}</td>
                                <td className="channel_performance_table_cell">
                                  <Button
                                    className="back-button btn-height"
                                    onClick={() => {
                                      const params = shop_domain ? `?shop_domain=${shop_domain}` : '';
                                      window.open(
                                        `${app_url}/api/subscriptions/invoices/${invoice._id}/download${params}`,
                                        '_blank'
                                      );
                                    }}
                                  >
                                    Download
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {invoices.length > invoicesPerPage && (
                        <div className="d-flex justify-content-end mt-3 me-2">
                          <Pagination className="mb-0">
                            <Pagination.Prev
                              onClick={() => setInvoicePage((prev) => Math.max(1, prev - 1))}
                              disabled={safeInvoicePage === 1}
                            />
                            {Array.from({ length: totalInvoicePages }, (_, i) => i + 1).map((page) => (
                              <Pagination.Item
                                key={page}
                                active={page === safeInvoicePage}
                                onClick={() => setInvoicePage(page)}
                              >
                                {page}
                              </Pagination.Item>
                            ))}
                            <Pagination.Next
                              onClick={() => setInvoicePage((prev) => Math.min(totalInvoicePages, prev + 1))}
                              disabled={safeInvoicePage === totalInvoicePages}
                            />
                          </Pagination>
                        </div>
                      )}
                    </>
                  )}
              </div>
            </div>
          )}
        </div>
        </div>
      </Container>

        {/* Add Payment Method Modal */}
        <Modal show={showAddPaymentModal} onHide={() => setShowAddPaymentModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Add Payment Method</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Verify your card to save it for future use</p>
            <ul className="small text-muted mb-2">
              <li>A temporary hold will be placed and automatically refunded</li>
            </ul>
            <p className="text-muted small mb-0">
              Payment secured by Razorpay
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button className="sxi_primary_blue_btn btn-height" onClick={handleAddPaymentMethod} disabled={addingPaymentMethod}>
              {addingPaymentMethod ? 'Processing...' : 'Continue'}
            </Button>
            <Button className="back-button btn-height" onClick={() => setShowAddPaymentModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Upgrade Plan Modal */}
        <Modal
          show={showUpgradeModal}
          onHide={() => {
            setShowUpgradeModal(false);
            setUpgradingPlanId(null);
          }}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Upgrade Your Plan</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-4">
              Upgrade to a higher plan to unlock more features. On upgrade, a fresh billing cycle starts with full plan payment.
            </p>
            {getUpgradeablePlans().length === 0 ? (
              <Alert variant="info">No upgrade plans available at the moment.</Alert>
            ) : (
              <PlanCarousel
                plans={getUpgradeablePlans().map(toPlanCardModel)}
                // The card carries the view model, so the handler maps back to
                // the raw plan doc the upgrade call needs.
                onSelect={(card) => {
                  const plan = getUpgradeablePlans().find((p) => normalizeId(p._id) === card.id);
                  if (plan) handleUpgrade(plan);
                }}
                busyPlanId={upgrading ? normalizeId(upgradingPlanId) : null}
                ctaLabel="Upgrade Now"
                // Same constant as the Active Plan card above, and the same
                // value the setup wizard passes — now that the carousel
                // actually forwards it, omitting it here would have made this
                // the one plan card in the product still rendering indigo.
                accent={BILLING_ACCENT}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button className="back-button btn-height" onClick={() => setShowUpgradeModal(false)}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
    </div>
  );
};

export default SubscriptionManagement;
